import { ApiResponse, PaginatedRequest } from '../types/api';
import {
  CreateCompanyRequest,
  CreateCompanyResponse,
  CreateDocumentTypeRequest,
  CreateDocumentTypeResponse,
  CreateCompanyGroupRequest,
  CreateCompanyGroupResponse,
  DeleteCompanyGroupResponse,
  FetchCompanyGroupResponse,
  UpdateCompanyGroupRequest,
  UpdateCompanyGroupResponse,
  DocumentUploadRequest,
  DocumentUpdateRequest,
  FetchCompaniesResponse,
  FetchCompanyDocumentsResponse,
  FetchCompanyResponse,
  FetchDocumentTypeResponse,
  FetchCompanyStatusesResponse,
  UpdateCompanyRequest,
  UpdateCompanyResponse,
  ApproveCompanyResponse,
  RestoreCompanyResponse,
  DocumentOperationResponse,
  UploadDocumentResponse,
  GetAttachmentResponse,
  FetchCompanyGroupsResponse,
  FetchCompanyEmployeesResponse,
  FetchCompanyEmployeeResponse,
  CreateCompanyEmployeeRequest,
  CreateCompanyEmployeeResponse,
  UpdateCompanyEmployeeRequest,
  UpdateCompanyEmployeeResponse,
  DeleteCompanyEmployeeResponse,
  RestoreCompanyEmployeeResponse,
  ActivateCompanyEmployeeResponse,
  InactivateCompanyEmployeeResponse,
  FetchOnboardingEmployeesResponse,
  FetchCompanyDepartmentsResponse,
  CreateCompanyDepartmentRequest,
  CreateCompanyDepartmentResponse,
  FetchCompanyDepartmentResponse,
  UpdateCompanyDepartmentRequest,
  UpdateCompanyDepartmentResponse,
  DeleteCompanyDepartmentResponse,
  RestoreCompanyDepartmentResponse,
  FetchEmployeeStatusesResponse,
  FetchEmployeeMaritalStatusesResponse,
  FetchCsvTemplatesResponse,
  FetchCsvTemplateResponse,
  FetchCsvTemplateNameOptionsResponse,
  CreateCsvTemplateResponse,
  UpdateCsvTemplateResponse,
  DeleteCsvTemplateResponse,
  FetchPortfoliosResponse,
  ResendActivationLinkResponse,
  FetchCompanyWithdrawRequestsResponse,
  FetchCompanyWithdrawRequestResponse,
  CreateCompanyWithdrawRequestRequest,
  CreateCompanyWithdrawRequestResponse,
  UpdateCompanyWithdrawRequestRequest,
  UpdateCompanyWithdrawRequestResponse,
  ApproveCompanyWithdrawRequestResponse,
  RejectCompanyWithdrawRequestResponse,
  FetchB2bJournalsRequest,
  FetchB2bJournalsResponse,
} from '../types/b2tRatibiPlus';
import { TriggerAnalyticsCronRequest, TriggerAnalyticsCronResponse } from '../types/analyticsCron';
import { toQueryParams } from '../utils/request';
import { ApiService } from './common/ApiService';

class B2bThriftPlan extends ApiService {
  pathname = '/admin';
  // ---------------------- COMPANIES ----------------------
  fetchCompanyies = ({ ...data }: PaginatedRequest) =>
    this.get<FetchCompaniesResponse>(`${this.pathname}/thrift-companies?${toQueryParams(data)}`);

  fetchCompany = ({ companyId }: PaginatedRequest & { companyId: number }) =>
    this.get<FetchCompanyResponse>(`${this.pathname}/thrift-companies/${companyId}`);

  deleteCompany = async (id: number) => this.delete<FetchCompanyResponse>(`${this.pathname}/thrift-companies/${id}`);

  createCompany = async (Company: CreateCompanyRequest) =>
    this.post<CreateCompanyResponse, CreateCompanyRequest>(`${this.pathname}/thrift-companies`, Company);

  updateCompany = async ({ id, ...companyData }: UpdateCompanyRequest) =>
    this.put<UpdateCompanyResponse, Omit<UpdateCompanyRequest, 'id'>>(
      `${this.pathname}/thrift-companies/${id}`,
      companyData,
    );

  approveCompany = async (id: number) =>
    this.put<ApproveCompanyResponse, Record<string, never>>(`${this.pathname}/thrift-companies/${id}/approve`, {});

  restoreCompany = async (id: number) =>
    this.put<RestoreCompanyResponse, Record<string, never>>(`${this.pathname}/thrift-companies/${id}/restore`, {});

  resendActivetionLink = async (companyId: number) =>
    this.post<ResendActivationLinkResponse, { companyId: number }>(
      `${this.pathname}/thrift-companies/resend-activation-link`,
      { companyId },
    );

  fetchDocumentType = async () => this.get<FetchDocumentTypeResponse>(`${this.pathname}/thrift-company-document-types`);

  createDocumentType = async (payload: CreateDocumentTypeRequest) =>
    this.post<CreateDocumentTypeResponse, CreateDocumentTypeRequest>(
      `${this.pathname}/thrift-company-document-types`,
      payload,
    );

  fetchCompanyStatuses = async () => this.get<FetchCompanyStatusesResponse>(`${this.pathname}/thrift-company-statuses`);

  uploadCompanyDocument = async ({ companyId, file, documentTypeId }: DocumentUploadRequest) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentTypeId', String(documentTypeId));
    formData.append('companyId', String(companyId));

    return this.post<UploadDocumentResponse, FormData>(`${this.pathname}/thrift-company-documents/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  };

  updateCompanyDocument = async ({ id, documentTypeId }: DocumentUpdateRequest) =>
    this.put<DocumentOperationResponse, { documentTypeId: number }>(`${this.pathname}/thrift-company-documents/${id}`, {
      documentTypeId,
    });

  deleteCompanyDocument = async (id: number) =>
    this.delete<DocumentOperationResponse>(`${this.pathname}/thrift-company-documents/${id}`);

  fetchCompanyDocuments = async (companyId: number) =>
    this.get<FetchCompanyDocumentsResponse>(`${this.pathname}/thrift-company-documents/company/${companyId}`);

  getAttachment = async (id: number) => this.get<GetAttachmentResponse>(`${this.pathname}/thrift-attachments/${id}`);

  // ---------------------- COMPANY GROUPS ----------------------
  fetchCompanyGroups = async ({ companyId, ...params }: { companyId: number } & PaginatedRequest) => {
    const url = `${this.pathname}/thrift-company-groups/company/${companyId}?${toQueryParams(params)}`;
    return this.get<FetchCompanyGroupsResponse>(url);
  };

  fetchCompanyGroup = async (companyId: number, groupId: number) =>
    this.get<FetchCompanyGroupResponse>(`${this.pathname}/thrift-company-groups/company/${companyId}/group/${groupId}`);

  createCompanyGroup = async (companyId: number, payload: CreateCompanyGroupRequest) =>
    this.post<CreateCompanyGroupResponse, CreateCompanyGroupRequest>(
      `${this.pathname}/thrift-company-groups/company/${companyId}`,
      payload,
    );

  updateCompanyGroup = async (companyId: number, groupId: number, payload: UpdateCompanyGroupRequest) =>
    this.put<UpdateCompanyGroupResponse, UpdateCompanyGroupRequest>(
      `${this.pathname}/thrift-company-groups/company/${companyId}/group/${groupId}`,
      payload,
    );

  deleteCompanyGroup = async (companyId: number, groupId: number) =>
    this.delete<DeleteCompanyGroupResponse>(
      `${this.pathname}/thrift-company-groups/company/${companyId}/group/${groupId}`,
    );

  uploadCompanyGroupsCsv = async (companyId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    return this.post<ApiResponse, FormData>(
      `${this.pathname}/thrift-company-groups/company/${companyId}/upload-csv`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
  };

  // ---------------------- COMPANY EMPLOYEES ----------------------
  fetchCompanyEmployees = async ({ companyId, ...params }: { companyId: number } & PaginatedRequest) => {
    const queryString = toQueryParams(params);
    const url = `${this.pathname}/thrift-company-group-employees/company/${companyId}${
      queryString ? `?${queryString}` : ''
    }`;
    return this.get<FetchCompanyEmployeesResponse>(url);
  };

  createCompanyEmployee = async (companyId: number, payload: CreateCompanyEmployeeRequest) =>
    this.post<CreateCompanyEmployeeResponse, CreateCompanyEmployeeRequest>(
      `${this.pathname}/thrift-company-group-employees/company/${companyId}`,
      payload,
    );

  fetchCompanyEmployee = async (companyId: number, employeeId: number) =>
    this.get<FetchCompanyEmployeeResponse>(
      `${this.pathname}/thrift-company-group-employees/company/${companyId}/${employeeId}`,
    );

  updateCompanyEmployee = async (companyId: number, employeeId: number, payload: UpdateCompanyEmployeeRequest) =>
    this.put<UpdateCompanyEmployeeResponse, UpdateCompanyEmployeeRequest>(
      `${this.pathname}/thrift-company-group-employees/company/${companyId}/${employeeId}`,
      payload,
    );

  deleteCompanyEmployee = async (companyId: number, employeeId: number) =>
    this.delete<DeleteCompanyEmployeeResponse>(
      `${this.pathname}/thrift-company-group-employees/company/${companyId}/${employeeId}`,
    );

  restoreCompanyEmployee = async (companyId: number, employeeId: number) =>
    this.put<RestoreCompanyEmployeeResponse, Record<string, never>>(
      `${this.pathname}/thrift-company-group-employees/company/${companyId}/${employeeId}/restore`,
      {},
    );

  activateCompanyEmployee = async (companyId: number, employeeId: number) =>
    this.put<ActivateCompanyEmployeeResponse, Record<string, never>>(
      `${this.pathname}/thrift-company-group-employees/company/${companyId}/${employeeId}/activate`,
      {},
    );

  inactivateCompanyEmployee = async (companyId: number, employeeId: number) =>
    this.put<InactivateCompanyEmployeeResponse, Record<string, never>>(
      `${this.pathname}/thrift-company-group-employees/company/${companyId}/${employeeId}/inactive`,
      {},
    );

  uploadCompanyEmployeesCsv = async (companyId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    return this.post<ApiResponse, FormData>(
      `${this.pathname}/thrift-company-group-employees/company/${companyId}/upload-csv`,
      formData,
    );
  };

  // ---------------------- ONBOARDING EMPLOYEES ----------------------
  fetchOnboardingEmployees = async ({ companyId, ...params }: { companyId: number } & PaginatedRequest) => {
    const queryString = toQueryParams(params);
    const url = `${this.pathname}/thrift-onboarding-employees/company/${companyId}${
      queryString ? `?${queryString}` : ''
    }`;
    return this.get<FetchOnboardingEmployeesResponse>(url);
  };

  // ---------------------- COMPANY DEPARTMENTS ----------------------
  fetchCompanyDepartments = async ({ companyId, ...data }: { companyId: number } & PaginatedRequest) => {
    const url = `${this.pathname}/thrift-company-departments/company/${companyId}?${toQueryParams(data)}`;
    return this.get<FetchCompanyDepartmentsResponse>(url);
  };

  fetchCompanyWithdrawRequests = async ({ companyId, ...data }: { companyId: number } & PaginatedRequest) => {
    const url = `${this.pathname}/thrift-company-withdraw-requests/company/${companyId}?${toQueryParams(data)}`;
    return this.get<FetchCompanyWithdrawRequestsResponse>(url);
  };

  fetchCompanyWithdrawRequest = async (companyId: number, id: number) => {
    const url = `${this.pathname}/thrift-company-withdraw-requests/company/${companyId}/${id}`;
    return this.get<FetchCompanyWithdrawRequestResponse>(url);
  };

  createCompanyWithdrawRequest = async (companyId: number, payload: CreateCompanyWithdrawRequestRequest) => {
    const url = `${this.pathname}/thrift-company-withdraw-requests/company/${companyId}`;
    return this.post<CreateCompanyWithdrawRequestResponse, CreateCompanyWithdrawRequestRequest>(url, payload);
  };

  updateCompanyWithdrawRequest = async (
    companyId: number,
    id: number,
    payload: UpdateCompanyWithdrawRequestRequest,
  ) => {
    const url = `${this.pathname}/thrift-company-withdraw-requests/company/${companyId}/${id}`;
    return this.put<UpdateCompanyWithdrawRequestResponse, UpdateCompanyWithdrawRequestRequest>(url, payload);
  };

  approveCompanyWithdrawRequest = async (companyId: number, id: number) => {
    const url = `${this.pathname}/thrift-company-withdraw-requests/company/${companyId}/${id}/approve`;
    return this.put<ApproveCompanyWithdrawRequestResponse, Record<string, never>>(url, {});
  };

  rejectCompanyWithdrawRequest = async (companyId: number, id: number) => {
    const url = `${this.pathname}/thrift-company-withdraw-requests/company/${companyId}/${id}/reject`;
    return this.put<RejectCompanyWithdrawRequestResponse, Record<string, never>>(url, {});
  };

  createCompanyDepartment = async (companyId: number, payload: CreateCompanyDepartmentRequest) =>
    this.post<CreateCompanyDepartmentResponse, CreateCompanyDepartmentRequest>(
      `${this.pathname}/thrift-company-departments/company/${companyId}`,
      payload,
    );

  fetchCompanyDepartment = async (companyId: number, departmentId: number) =>
    this.get<FetchCompanyDepartmentResponse>(
      `${this.pathname}/thrift-company-departments/company/${companyId}/${departmentId}`,
    );

  updateCompanyDepartment = async (companyId: number, departmentId: number, payload: UpdateCompanyDepartmentRequest) =>
    this.put<UpdateCompanyDepartmentResponse, UpdateCompanyDepartmentRequest>(
      `${this.pathname}/thrift-company-departments/company/${companyId}/${departmentId}`,
      payload,
    );

  deleteCompanyDepartment = async (companyId: number, departmentId: number) =>
    this.delete<DeleteCompanyDepartmentResponse>(
      `${this.pathname}/thrift-company-departments/company/${companyId}/${departmentId}`,
    );

  restoreCompanyDepartment = async (companyId: number, departmentId: number) =>
    this.put<RestoreCompanyDepartmentResponse, Record<string, never>>(
      `${this.pathname}/thrift-company-departments/company/${companyId}/${departmentId}/restore`,
      {},
    );

  // ---------------------- EMPLOYEE STATUSES ----------------------
  fetchEmployeeStatuses = async () =>
    this.get<FetchEmployeeStatusesResponse>(`${this.pathname}/thrift-company-employee-statuses/`);

  // ---------------------- EMPLOYEE MARITAL STATUSES ----------------------
  fetchEmployeeMaritalStatuses = async () =>
    this.get<FetchEmployeeMaritalStatusesResponse>(`${this.pathname}/thrift-company-employee-marital-statuses/`);

  // ---------------------- CSV TEMPLATES ----------------------
  fetchCsvTemplates = async ({ ...data }: PaginatedRequest) =>
    this.get<FetchCsvTemplatesResponse>(`${this.pathname}/thrift-default-csv-templates?${toQueryParams(data)}`);

  fetchCsvTemplate = async (id: number) =>
    this.get<FetchCsvTemplateResponse>(`${this.pathname}/thrift-default-csv-templates/${id}`);

  fetchCsvTemplateNameOptions = async () =>
    this.get<FetchCsvTemplateNameOptionsResponse>(
      `${this.pathname}/thrift-default-csv-templates/default-csv-templates-names/name-options`,
    );

  createCsvTemplate = async ({ name, file }: { name: string; file: File }) => {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('file', file);

    return this.post<CreateCsvTemplateResponse, FormData>(
      `${this.pathname}/thrift-default-csv-templates/upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
  };

  updateCsvTemplate = async (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    return this.put<UpdateCsvTemplateResponse, FormData>(
      `${this.pathname}/thrift-default-csv-templates/${id}`,
      formData,
    );
  };

  deleteCsvTemplate = async (id: number) =>
    this.delete<DeleteCsvTemplateResponse>(`${this.pathname}/thrift-default-csv-templates/${id}`);

  // ---------------------- PORTFOLIOS ----------------------
  fetchPortfolios = async ({ ...data }: PaginatedRequest) =>
    this.get<FetchPortfoliosResponse>(`${this.pathname}/thrift-admin/portfolio/?${toQueryParams(data)}`);

  // ---------------------- B2B LEDGER JOURNALS ----------------------
  fetchB2bJournals = async (params?: FetchB2bJournalsRequest) =>
    this.get<FetchB2bJournalsResponse>(
      `${this.pathname}/ledger/b2b-journals${params ? `?${toQueryParams(params)}` : ''}`,
    );

  // ---------------------- ANALYTICS CRON ----------------------
  /**
   * Run an analytics cron now instead of waiting for its schedule.
   *
   * Resolves as soon as the job has STARTED. It never reports the outcome — a full run
   * outlives the request, so the result appears in the thriftplan logs.
   */
  triggerAnalyticsCron = async (payload: TriggerAnalyticsCronRequest) =>
    this.post<TriggerAnalyticsCronResponse, TriggerAnalyticsCronRequest>(
      `${this.pathname}/thrift-analytics-cron/trigger`,
      payload,
    );

  // Treasury: redux/features/treasury/treasury.apiSlice.ts (RTK Query).
}

export const b2bThriftPlan = new B2bThriftPlan();
